import type { ReactNode } from 'react';
import type { DetailBreadcrumb } from './types';
import { styles } from './styles';

/**
 * The breadcrumb nav. Each entry is a button (when `href` or `onClick` is
 * provided) or a span (the last entry, which is the current page).
 *
 * Separators are non-interactive spans — they are presentational, not part
 * of the breadcrumb path a user would click through.
 */
export function Breadcrumbs({ breadcrumbs }: { breadcrumbs?: ReadonlyArray<DetailBreadcrumb> }): ReactNode {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;
  return (
    <nav aria-label="Breadcrumbs" className={styles.breadcrumbs}>
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const content = crumb.label;
        if (isLast) {
          return (
            <span key={crumb.label} className={styles.breadcrumbCurrent}>
              {content}
            </span>
          );
        }
        if (crumb.onClick) {
          return (
            <span key={crumb.label} className={styles.breadcrumbItem}>
              <button type="button" onClick={crumb.onClick} className={styles.breadcrumbLink}>
                {content}
              </button>
              <span className={styles.breadcrumbSeparator}>/</span>
            </span>
          );
        }
        return (
          <span key={crumb.label} className={styles.breadcrumbItem}>
            <a href={crumb.href ?? '#'} className={styles.breadcrumbLink}>
              {content}
            </a>
            <span className={styles.breadcrumbSeparator}>/</span>
          </span>
        );
      })}
    </nav>
  );
}